// The v2 board's columns (GRO-276): # · [Fit · Engmt] · Account · Why now · Vertical · Relationship ·
// AE · Last touch · Usage 30d · Funding. One line per cell at 34px rows, sized so
// nothing truncates at 1440 and, with scores on, the board still fits it with no side-scroll; narrower, DenseTable drops columns in columnFit.ts's order. Every
// cell but # and Account opens a hover card with the detail.
//
// Key, label and full-value accessor come from boardV2Csv.ts. The CSV keeps two columns the table
// folds away: Our VC (in the Funding hover) and Bucket (in the Fit and Engagement hovers).

import type { ReactNode } from 'react';
import type { BookColumn, CellContext } from './columns';
import {
  RELATIONSHIP_RANK, bestSignalScore, employeesLabel, firstName, fundingShort, lastTouchReading,
  relationshipPill, usageReading, ventureSummary, verticalReading, whyNowCell,
} from './boardCells';
import type { PillTone } from './boardCells';
import {
  FundingCard, LastTouchCard, RelationshipCard, ScoreCard, UsageCard, VentureCard,
  VerticalCard, WhyNowCard,
} from './boardHoverCards';
import { AccountChips, AccountHover, chipsAttribute } from './AccountHover';
import { requestFlagFacts } from './accountCard';
import type { RequestFlags } from './accountCard';
import { SharedSlackChip } from './AccountFlagChips';
import { RequestFlagChips } from './RequestFlagNotes';
import { hasSharedSlack, pastOpportunityChip, pastOpportunityOf } from './accountFlags';
import { meetingBooked } from './boardPref';
import { lastOutbound, recentLostDeal } from './derived';
import type { RankedAccount } from './derived';
import { fundingSortValue, relationshipSortValue } from './funding';
import { boardV2CsvColumns } from './boardV2Csv';
import { engagementSortValue, fitSortValue, formatScore, scoreLevel, scoreRankOf } from './scores';
import { HoverCard } from '../components/HoverCard';
import { companyUrl, displayAccountName } from '../lib/format';

function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return <span className={'pill ' + tone}>{children}</span>;
}

export function Dash() {
  return <span className="dash">—</span>;
}

// Dev only: `?hover=<account id>:<column>` pins one card open, for screenshots.
function isPinned(context: CellContext, row: RankedAccount, column: string): boolean {
  return context.pinnedHover === row.id + ':' + column;
}

// Name, the employee count in small grey, the Slack and past-opp chips (the Accounts page style),
// then glyphs (L lost deal, ● booked) and the Auto pill. One hover card holds the full name, size,
// industry and every flag. `request` and `competingTotal` are Available's own request flags.
export function AccountCell({
  row, context, request = null, competingTotal,
}: { row: RankedAccount; context: CellContext; request?: RequestFlags | null; competingTotal?: number }) {
  const href = companyUrl(row.domain, row.website);
  const lost = recentLostDeal(row, context.today);
  const employees = employeesLabel(row);
  const past = pastOpportunityOf(row);
  const requestFacts = requestFlagFacts(request, competingTotal);
  const displayName = displayAccountName(row.account);

  // How many chips the cell draws, for the "+N" chip a narrow column collapses them into.
  const chipFlags = [hasSharedSlack(row), Boolean(past), Boolean(lost), meetingBooked(row), Boolean(row.auto_lane)];
  const chipCount = chipFlags.filter(Boolean).length + requestFacts.length;
  return (
    <AccountHover row={row} name={row.account} today={context.today} requestFacts={requestFacts} forceOpen={isPinned(context, row, 'account')}>
      <span className="dacct" data-chips={chipsAttribute(chipCount)}>
        {href
          ? <a className="dname" href={href} target="_blank" rel="noopener">{displayName}</a>
          : <span className="dname">{displayName}</span>}
        {employees ? <span className="dsub">{employees}</span> : null}
        <AccountChips count={chipCount}>
          {hasSharedSlack(row) ? <SharedSlackChip url={row.pylon_slack_url} /> : null}
          {past ? <span className="ovflag past">{pastOpportunityChip(past)}</span> : null}
          {lost ? <span className="glyph lost" aria-label={`Closed lost ${lost.when}`}>L</span> : null}
          {meetingBooked(row) ? <span className="glyph booked" aria-label="Meeting booked">●</span> : null}
          {row.auto_lane ? <span className="pill info fixed autopill">Auto</span> : null}
          <RequestFlagChips request={request} competingTotal={competingTotal} />
        </AccountChips>
      </span>
    </AccountHover>
  );
}

// A tone dot, a phrase of at most five words from best_signal, and the age pill.
export function WhyNowCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const reading = whyNowCell(row);
  if (!reading) return <Dash />;
  return (
    <HoverCard card={() => <WhyNowCard row={row} />} label={`Why now: ${reading.phrase}`} className="dpills" forceOpen={isPinned(context, row, 'why_now')}>
      <span className={'tonedot ' + reading.tone} aria-hidden="true" />
      <span className={reading.pass ? 'shortwhy pass ' + reading.tone : 'shortwhy'}>{reading.phrase}</span>
      {reading.age ? <span className="pill neutral fixed">{reading.age}</span> : null}
    </HoverCard>
  );
}

export function VerticalCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const vertical = verticalReading(row);
  if (!vertical) return <Dash />;
  return (
    <HoverCard card={() => <VerticalCard row={row} />} label={`Vertical: ${vertical.title}`} forceOpen={isPinned(context, row, 'vertical')}>
      <Pill tone="neutral">{vertical.label}</Pill>
    </HoverCard>
  );
}

export function RelationshipCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const pill = relationshipPill(row);
  return (
    <HoverCard card={() => <RelationshipCard row={row} />} label={`Relationship: ${pill.label}`} forceOpen={isPinned(context, row, 'relationship_pill')}>
      <Pill tone={pill.tone}>{pill.label}</Pill>
    </HoverCard>
  );
}

function LastTouchCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const last = lastTouchReading(row, context.today);
  if (!last) return <span className="zero">never</span>;
  const isStale = last.days != null && last.days >= context.staleDays;
  return (
    <HoverCard card={() => <LastTouchCard row={row} />} label={`Last touch ${last.day}`} className="dpills" forceOpen={isPinned(context, row, 'last_out')}>
      <span className="mono">{last.day}</span>
      {last.age ? <span className={'dage' + (isStale ? ' stale' : '')}>{last.age}</span> : null}
      {last.other_rep ? <span className="otherrep">{last.other_rep}</span> : null}
    </HoverCard>
  );
}

function Trend({ percent }: { percent: number | null }) {
  if (percent == null || percent === 0) return null;
  return <span className={'trend ' + (percent > 0 ? 'up' : 'down')}>{percent > 0 ? '▲' : '▼'}{Math.abs(percent)}%</span>;
}

export function UsageCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const usage = usageReading(row);
  if (!usage) return <Dash />;
  return (
    <HoverCard card={() => <UsageCard row={row} />} label={`Usage ${usage.text}`} forceOpen={isPinned(context, row, 'credits_30')}>
      {usage.text} <Trend percent={usage.trend_pct} />
    </HoverCard>
  );
}

// "Series B $40M · 2mo". Under 90 days: stage in bold ink, age in the accent; older is muted.
// One of our funds on the cap table adds a dot (accent when it was in the latest round); the
// funds themselves are in the hover, which is the old Our VC column's card under the rounds.
function FundingCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const funding = fundingShort(row);
  const venture = ventureSummary(row);
  const lead = venture.kind === 'funds' ? venture.ties[0]! : null;
  const vcMark = lead
    ? <span className={'vcdot' + (lead.in_latest ? ' accent' : '')} aria-label={`Our fund: ${lead.short_name}`}>●</span>
    : null;
  if (!funding && !lead) return <Dash />;
  const label = [funding ? `Funding: ${funding.main}` : 'Funding: none on file', lead ? `our fund ${lead.fund}` : null]
    .filter(Boolean).join(' · ');
  return (
    <HoverCard card={() => <FundingCard row={row} />} label={label} className={'fundshort' + (funding?.is_recent ? ' recent' : '')} forceOpen={isPinned(context, row, 'funding')}>
      {funding ? <span className="fundmain">{funding.main}</span> : <span className="dash">—</span>}
      {funding?.age ? <span className="fundage">· {funding.age}</span> : null}
      {vcMark}
    </HoverCard>
  );
}

// The board without scores keeps main's Funding cell (no fund dot) and its Our VC column, so a
// strategist sees no change while scores run in shadow mode (GRO-460).
function PlainFundingCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const funding = fundingShort(row);
  if (!funding) return <Dash />;
  return (
    <HoverCard card={() => <FundingCard row={row} withFunds={false} />} label={`Funding: ${funding.main}`} className={'fundshort' + (funding.is_recent ? ' recent' : '')} forceOpen={isPinned(context, row, 'funding')}>
      <span className="fundmain">{funding.main}</span>
      {funding.age ? <span className="fundage">· {funding.age}</span> : null}
    </HoverCard>
  );
}

// The strongest fund in plain text, accent when it was in the latest round. "—" none, "?" unknown.
function VentureCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const summary = ventureSummary(row);
  const pinned = isPinned(context, row, 'relationship');
  if (summary.kind === 'none') {
    return <HoverCard card={() => <VentureCard row={row} />} label="None of our funds" forceOpen={pinned}><span className="dash">—</span></HoverCard>;
  }
  if (summary.kind === 'unknown') {
    return <HoverCard card={() => <VentureCard row={row} />} label="Couldn’t find out" forceOpen={pinned}><span className="unknownmark">?</span></HoverCard>;
  }
  const lead = summary.ties[0]!;
  const more = summary.ties.length - 1;
  return (
    <HoverCard card={() => <VentureCard row={row} />} label={`Our funds: ${lead.fund}`} forceOpen={pinned}>
      <span className={'vcname' + (lead.in_latest ? ' accent' : '')}>{lead.short_name}</span>
      {more > 0 ? <span className="dage">+{more}</span> : null}
    </HoverCard>
  );
}

// Fit and Engagement (GRO-381): the number, coloured by its level (green high, black mid, red low;
// cut-offs in scores.ts), with the breakdown on hover. No missing-data marker here: the hover has it.
export function ScoreCell({ row, context, kind }: { row: RankedAccount; context: CellContext; kind: 'fit' | 'engagement' }) {
  const score = row.score;
  const value = kind === 'fit' ? score?.fit_score : score?.engagement_score;
  if (!score || value == null) return <Dash />;
  const level = scoreLevel(kind, value);
  const name = kind === 'fit' ? 'Fit' : 'Engagement';
  return (
    <HoverCard card={() => <ScoreCard row={row} kind={kind} />} label={`${name} ${formatScore(value)}`} forceOpen={isPinned(context, row, kind + '_score')}>
      <span className={'scorenum scorecell ' + (level ?? '')}>{formatScore(value)}</span>
    </HoverCard>
  );
}

// What each table column adds to its CSV column (boardV2Csv.ts): width, hint, sort and cell.
// `label` overrides the CSV's header where the table needs a different one.
// Fit and Engmt are the same width, wide enough for the "Engmt" header and its sort arrow.
export const SCORE_WIDTH_PX = 54;

type ColumnView = Omit<BookColumn, 'key' | 'label' | 'accessor'> & { label?: string };

const RANK_VIEW: ColumnView = {
  width: '38px', numeric: true,
  hint: 'Scout’s order for this morning.',
  sortValue: row => row.ranking ?? null,
  cell: row => (row.ranking == null ? <Dash /> : row.ranking),
};

// With scores the # is the score order (2 x Fit + Engagement, re-counted over the rows shown),
// whichever column the table is sorted by.
const SCORE_RANK_VIEW: ColumnView = {
  width: '38px', numeric: true,
  hint: 'Rank by 2 x Fit + Engagement among the rows shown. Blank when there is no score.',
  sortValue: row => scoreRankOf(row),
  cell: row => (scoreRankOf(row) == null ? <Dash /> : scoreRankOf(row)),
};

const COLUMN_VIEWS: Record<string, ColumnView> = {
  rank: SCORE_RANK_VIEW,

  account: {
    width: '200px',
    hint: 'Employees beside the name. # shared Slack · L lost deal · ● meeting booked.',
    sortValue: row => row.account,
    cell: (row, context) => <AccountCell row={row} context={context} />,
  },
  why_now: {
    width: '222px',
    hint: 'The reason to reach out now. Green is fresh, orange is older or evergreen. Hover for the source.',
    sortValue: row => bestSignalScore(row),
    cell: (row, context) => <WhyNowCell row={row} context={context} />,
  },
  vertical: {
    width: '112px',
    hint: 'The OB Engine’s enriched sector, else Salesforce’s industry.',
    sortValue: row => verticalReading(row)?.label ?? null,
    cell: (row, context) => <VerticalCell row={row} context={context} />,
  },
  relationship_pill: {
    width: '140px',
    hint: 'Customer › Open opp › Paid self-serve › Self-serve › Closed Lost › Touched › Net new.',
    sortValue: row => RELATIONSHIP_RANK[relationshipPill(row).kind],
    cell: (row, context) => <RelationshipCell row={row} context={context} />,
  },
  account_owner: {
    width: '72px',
    hint: 'The Salesforce account owner.',
    sortValue: row => row.account_owner,
    cell: row => (row.account_owner ? <span className="dclip">{firstName(row.account_owner)}</span> : <Dash />),
  },
  last_out: {
    width: '100px',
    hint: 'The last outbound touch by anybody. Initials: a rep other than the owner sent it.',
    sortValue: row => lastOutbound(row)?.day ?? null,
    cell: (row, context) => <LastTouchCell row={row} context={context} />,
  },
  credits_30: {
    width: '90px', numeric: true,
    hint: 'Playground credits in the last 30 days, and the change on the 30 before.',
    sortValue: row => row.credits_30,
    cell: (row, context) => <UsageCell row={row} context={context} />,
  },
  fit_score: {
    width: `${SCORE_WIDTH_PX}px`, numeric: true,
    hint: 'Fit, 0 to 100: should they buy voice AI from us. Green high, red low. Hover for the breakdown and the bucket.',
    sortValue: fitSortValue,
    cell: (row, context) => <ScoreCell row={row} context={context} kind="fit" />,
  },
  engagement_score: {
    width: `${SCORE_WIDTH_PX}px`, numeric: true,
    hint: 'Engagement, 0 to 100: are they paying attention now. Green high, red low. Hover for the breakdown and the bucket.',
    sortValue: engagementSortValue,
    cell: (row, context) => <ScoreCell row={row} context={context} kind="engagement" />,
  },
  funding: {
    width: '154px',
    hint: 'Latest round and amount, and how long ago. Bold: under 90 days. A dot: one of our funds is on the cap table (green: in the latest round). Hover for the rounds and our funds.',
    sortValue: fundingSortValue,
    cell: (row, context) => <FundingCell row={row} context={context} />,
  },
};

// Without scores (a strategist while scoring runs in shadow mode, GRO-460) the board is main's from
// before scoring: Why now at 250px, Funding without the fund dot, and Our VC as its own column.
const PLAIN_COLUMN_VIEWS: Record<string, ColumnView> = {
  rank: RANK_VIEW,
  why_now: { ...COLUMN_VIEWS.why_now!, width: '250px' },
  funding: {
    width: '150px',
    hint: 'Latest round and amount, and how long ago. Bold: under 90 days.',
    sortValue: fundingSortValue,
    cell: (row, context) => <PlainFundingCell row={row} context={context} />,
  },
  relationship: {
    width: '122px',
    hint: 'Our strongest fund on their cap table. Green: in their latest round. — none of ours · ? unknown.',
    sortValue: relationshipSortValue,
    cell: (row, context) => <VentureCell row={row} context={context} />,
  },
};

// The v2 board's columns, in the CSV's order, without the CSV-only ones (no view): with scores, Our
// VC and Bucket fold into hovers; without, only Bucket is CSV-only (and it is out of the CSV too).
export function boardV2Columns(includeScores = false): BookColumn[] {
  const views = includeScores ? COLUMN_VIEWS : { ...COLUMN_VIEWS, ...PLAIN_COLUMN_VIEWS };
  return boardV2CsvColumns(includeScores)
    .filter(csvColumn => views[csvColumn.key] !== undefined)
    .map(csvColumn => ({ ...csvColumn, ...views[csvColumn.key]! }));
}

// Fit and Engmt (54px each) for Available, the leadership Accounts table and the AE table:
// the board's own cells, sort and hover. Drawn only where the board would draw them.
export function compactScoreColumns(): BookColumn[] {
  return boardV2Columns(true).filter(column => column.key === 'fit_score' || column.key === 'engagement_score');
}

// The # column the scored tables draw first (score order, 2 x Fit + Engmt).
export function scoreRankColumn(): BookColumn {
  return boardV2Columns(true).find(column => column.key === 'rank')!;
}
